import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Star, MapPin, Briefcase, ShieldCheck, Loader2 } from "lucide-react";

export default function CleanerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/users/${id}/public`).then((r) => setData(r.data)); }, [id]);
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-ocean" /></div>;

  const u = data.user;
  const initials = (u.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="space-y-6 max-w-3xl" data-testid="public-profile">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-sapphire" data-testid="back-button">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="border border-border rounded-md bg-white p-6">
        <div className="flex items-start gap-5">
          {u.avatar ? (
            <img src={u.avatar} alt="" className="w-20 h-20 rounded-md object-cover border border-border" />
          ) : (
            <div className="w-20 h-20 rounded-md bg-ocean text-white flex items-center justify-center text-2xl font-bold font-heading">{initials}</div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="font-heading font-black text-2xl tracking-tight text-sapphire">{u.name}</h1>
              {u.background_check_status === "approved" && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded"><ShieldCheck className="w-3 h-3" /> Verified</span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-1">
              {u.service_area && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{u.service_area}</span>}
              <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{data.jobs_completed} jobs completed</span>
              {data.rating != null && <span className="flex items-center gap-1 text-amber-600 font-semibold"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />{data.rating} ({data.review_count})</span>}
            </div>
            {u.bio && <p className="text-sm text-slate-600 mt-3">{u.bio}</p>}
            {u.available_weekdays && (
              <div className="flex gap-1 mt-3" data-testid="profile-availability">
                {["M", "T", "W", "T", "F", "S", "S"].map((l, i) => (
                  <span key={i} className={`w-7 h-7 rounded text-xs font-semibold flex items-center justify-center ${u.available_weekdays.includes(i) ? "bg-sapphire text-white" : "bg-slate-100 text-slate-400"}`}>{l}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-heading font-bold text-xl text-sapphire mb-3">Reviews {data.review_count > 0 && `(${data.review_count})`}</h2>
        {data.reviews.length === 0 ? (
          <div className="border border-dashed border-border rounded-md p-8 text-center text-muted-foreground text-sm">No reviews yet.</div>
        ) : (
          <div className="space-y-3">
            {data.reviews.map((r) => (
              <div key={r.id} className="border border-border rounded-md bg-white p-4" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sapphire text-sm">{r.pm_name}</span>
                  <span className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}
                  </span>
                </div>
                {r.comment && <p className="text-sm text-slate-600 mt-1">{r.comment}</p>}
                <div className="text-[11px] text-muted-foreground mt-1">{new Date(r.created_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
